import * as React from 'react';
import { CardAside, CardEmpty } from '@chess/api-dashboard/react';
import type { FilterLink } from '@chess/api-insights/charts';
import type { GameFilter, Phase, StageChange } from '@chess/api-model';
import { CHANGE_BANDS, type ChangeBand, type StageFlowResult } from './StageFlowAggregator.ts';
import './stage-flow.css';

export type StageFlowCardProps = { result: StageFlowResult; link?: FilterLink };

/**
 * "How each stage goes": one stacked bar per stage, in play order, of the
 * scanned games that reached it, by how the reader's score changed across
 * it — gained (green), gained a little (light green), held (grey), lost a
 * little (light red), lost (red) — with the games counted at the left and
 * the share that lost score at the right, the worst stage's in red. Every
 * segment opens the library filtered to its games (`stage-change`), so a
 * red band is a list of games to open. Presentational: its data comes as
 * props. The body of a wide card: the section draws its frame and title
 * (`registerCard`, @chess/api-dashboard).
 */

const STAGES: Array<{ stage: Phase; label: string }> = [
  { stage: 'opening', label: 'Opening' },
  { stage: 'middlegame', label: 'Middlegame' },
  { stage: 'ending', label: 'Ending' },
];
export const BAND_LABEL: Record<ChangeBand, string> = {
  gained: 'Gained score',
  'slightly-gained': 'Gained a little',
  held: 'Held',
  'slightly-lost': 'Lost a little',
  lost: 'Lost score',
};
const BAND_COLOUR: Record<ChangeBand, string> = {
  gained: 'var(--viz-win)',
  'slightly-gained': 'color-mix(in srgb, var(--viz-win) 45%, var(--viz-draw))',
  held: 'var(--viz-draw)',
  'slightly-lost': 'color-mix(in srgb, var(--viz-loss) 45%, var(--viz-draw))',
  lost: 'var(--viz-loss)',
};
/** The bands counted as score lost, for the share at the right. */
const LOST: readonly ChangeBand[] = ['slightly-lost', 'lost'];

const filterOf = (stage: Phase, band: ChangeBand): GameFilter => ({ stageChange: `${stage}-${band}` as StageChange });
const pct = (n: number, of: number) => (of ? Math.round((100 * n) / of) : 0);
const lostOf = (by: Record<ChangeBand, number>) => LOST.reduce((n, b) => n + by[b], 0);

export const StageFlowCard: React.FC<StageFlowCardProps> = ({ result, link }) => {
  const rows = STAGES.map((s) => ({ ...s, ...result.stages[s.stage] }));
  const any = rows.some((r) => r.games > 0);
  const worst = rows.reduce<(typeof rows)[number] | undefined>((w, r) => (r.games && (!w || pct(lostOf(r.by), r.games) > pct(lostOf(w.by), w.games)) ? r : w), undefined);
  return (
    <>
      <CardAside>score change across the stage, of the games that reached it</CardAside>
      {any ? (
        <>
          <div className="stage-flow-rows">
            <div className="stage-flow-row stage-flow-head" aria-hidden="true">
              <span />
              <span />
              <span className="stage-flow-against">lost score</span>
            </div>
            {rows.map((r) => {
              const lost = pct(lostOf(r.by), r.games);
              return (
                <div key={r.stage} className="stage-flow-row">
                  <span className="stage-flow-label">
                    {r.label} <span className="stage-flow-games">{r.games}</span>
                  </span>
                  <span className="stage-flow-bar" role="img" aria-label={`${r.label}: ${CHANGE_BANDS.map((b) => `${BAND_LABEL[b].toLowerCase()} ${r.by[b]}`).join(', ')} of ${r.games}`}>
                    {CHANGE_BANDS.filter((b) => r.by[b] > 0).map((b) => {
                      const filter = filterOf(r.stage, b);
                      const title = `${r.label}: ${BAND_LABEL[b].toLowerCase()} in ${r.by[b]} of ${r.games} games (${pct(r.by[b], r.games)}%). Opens them in the library.`;
                      const style = { flex: r.by[b], background: BAND_COLOUR[b] };
                      return link ? (
                        <a
                          key={b}
                          className="stage-flow-segment"
                          style={style}
                          href={link.href(filter)}
                          title={title}
                          data-track="overview.stage-flow.segment"
                          data-track-value={r.stage}
                          onClick={(e) => {
                            e.preventDefault();
                            link.open(filter);
                          }}
                        />
                      ) : (
                        <span key={b} className="stage-flow-segment" style={style} title={title} />
                      );
                    })}
                  </span>
                  <span className={`stage-flow-against${worst?.stage === r.stage && lost > 0 ? ' stage-flow-worst' : ''}`}>{r.games ? `${lost}%` : '—'}</span>
                </div>
              );
            })}
          </div>
          <ul className="legend-row stage-flow-legend">
            {CHANGE_BANDS.map((b) => (
              <li key={b}>
                <span className="legend-swatch" style={{ background: BAND_COLOUR[b] }} /> {BAND_LABEL[b]}
              </li>
            ))}
          </ul>
        </>
      ) : (
        <CardEmpty>No scanned games in the last 30 days.</CardEmpty>
      )}
    </>
  );
};
